import React from "react";
import {
  MdOutlineFormatListBulleted,
  MdOutlineMailOutline,
  MdOutlineTaskAlt,
} from "react-icons/md";

import { NavigationCard } from "../../../components/card/NavigationCard";
import { FolderGlyph } from "../../files/ui/FilesUi";
import { useGetFolderTemplatesQuery } from "../../../slices/DocumentsSlice";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetSequenceTemplatesQuery } from "../../../slices/EmailSequencesSlice";
import { useGetEmailTemplatesQuery } from "../../../slices/EmailTemplatesSlice";
import { useGetTemplatesQuery } from "../../../slices/TemplatesSlice";

const getTemplatesCountText = (count?: number) => {
  if (typeof count !== "number") {
    return "Loading templates...";
  }

  return `${count} ${count === 1 ? "template" : "templates"}`;
};

export const TemplatesPage: React.FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: taskTemplates } = useGetTemplatesQuery(
    { orgId, status: "all" },
    { skip: !orgId },
  );
  const { data: emailTemplates } = useGetEmailTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: sequenceTemplates } = useGetSequenceTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: folderTemplates } = useGetFolderTemplatesQuery(orgId, {
    skip: !orgId,
  });

  return (
    <div className={"mb-[120px] py-6 font-Manrope"}>
      <div className={"mb-6"}>
        <div className={"text-2xl font-semibold text-[#253747]"}>Templates</div>
        <div className={"mt-1 text-sm text-[#8A99AA]"}>
          Pick a template type to manage. Reusable templates save you from
          building work from scratch.
        </div>
      </div>

      <div className={"grid max-w-[740px] grid-cols-1 gap-5 md:grid-cols-2"}>
        <NavigationCard
          to={"task-templates"}
          title={"Subtask templates"}
          description={"Predefined checklists of subtasks for a work item."}
          meta={getTemplatesCountText(taskTemplates?.length)}
          icon={<MdOutlineTaskAlt className={"h-5 w-5"} />}
        />

        <NavigationCard
          to={"email-templates"}
          title={"Email templates"}
          description={"Single reusable email bodies with placeholders."}
          meta={getTemplatesCountText(emailTemplates?.length)}
          icon={<MdOutlineMailOutline className={"h-5 w-5"} />}
        />

        <NavigationCard
          to={"email-sequence-templates"}
          title={"Email sequence templates"}
          description={"Multi-step automated email sequences."}
          meta={getTemplatesCountText(sequenceTemplates?.length)}
          icon={<MdOutlineFormatListBulleted className={"h-5 w-5"} />}
        />

        <NavigationCard
          to={"folder-templates"}
          title={"Folder templates"}
          description={"The folders every client starts with in Files."}
          meta={getTemplatesCountText(folderTemplates?.length)}
          icon={<FolderGlyph size={20} />}
        />
      </div>
    </div>
  );
};
